import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { useChatStore, useAuthStore } from '@/store';
import ChatDetailedItem from '@/components/ChatDetailedItem';
import LoadMoreButton from '@/components/Buttons/LoadMoreButton';
import MetaHead from '@/components/MetaHead';
import { useTranslation } from '@/hooks/useTranslation';
import SignInCTAPanel from '@/components/Placeholders/SignInCTAPanel';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
  },
  content: {
    flex: 1,
    width: '100%',
  },
  centeredContent: {
    width: '100%',
    maxWidth: 800,
    alignSelf: 'center',
    paddingHorizontal: 20,
  },
});

const LibraryPage = React.memo(() => {
  const { t } = useTranslation();
  const userId = useAuthStore(state => state.user?.id);
  const hasUser = Boolean(userId);
  const allChats = useChatStore(state => state.chats);
  const chatPagination = useChatStore(state => state.pagination);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const fetchChats = useChatStore(state => state.fetchChats);

  // Final UI-level gate. Mirrors the sidebar filter so this page can never
  // display chats that should be hidden from history, even if upstream
  // filtering missed something:
  //   - Temporary chats: created with flags.isTemporary=true via the ghost
  //     icon. They're cleaned up on navigate-away from c/[id], but we
  //     defense-in-depth filter at render time to handle rehydrate-from-
  //     localStorage, in-flight fetchChats finishing after the backend
  //     delete, socket conversation_created racing the cleanup, etc.
  //   - Hidden tool-run backing conversations (see docs/TOOLS.md).
  const chats = useMemo(
    () => (allChats || []).filter(c => {
      if (c?.flags?.isTemporary === true) return false;
      if (c?.flags?.isToolRun === true && !c?.flags?.isToolPromotedToChat) return false;
      return true;
    }),
    [allChats]
  );

  const handleLoadMoreChats = async () => {
    if (isLoadingMore || !chatPagination.hasMore) return;
    
    try {
      setIsLoadingMore(true);
      const nextPage = chatPagination.current + 1;
      await fetchChats(nextPage);
    } catch (error) {
      console.error('Error loading more chats:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const renderItem = React.useCallback(({ item, index }) => (
    <View style={styles.centeredContent}>
      <ChatDetailedItem
        {...item}
        isLastItem={index === chats.length - 1}
      />
    </View>
  ), [chats.length]);

  const ListFooterComponent = React.useCallback(() => (
    chatPagination.hasMore ? (
      <View style={styles.centeredContent}>
        <LoadMoreButton
          onPress={handleLoadMoreChats}
          isLoading={isLoadingMore}
          isLarge={true}
        />
      </View>
    ) : null
  ), [chatPagination.hasMore, isLoadingMore]);

  if (!hasUser) {
    return (
      <View style={styles.container}>
        <MetaHead 
          title={t('pages.library.title')}
          description={t('pages.library.description')}
          noindex
        />
        <View style={[styles.content, styles.centeredContent]}>
          <SignInCTAPanel
            icon="book"
            source="library_index"
            title="Sign in to see your Library"
            subtitle="Your library is the full history of every chat you start with Gab. Create an account so we can save them across devices."
            benefits={[
              { icon: 'clock-rotate-left', label: 'Pick up any conversation where you left off.' },
              { icon: 'magnifying-glass', label: 'Search across every chat you have ever started.' },
              { icon: 'cloud', label: 'Sync your history across web, desktop, and mobile.' },
            ]}
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <MetaHead 
        title={t('pages.library.title')}
        description={t('pages.library.description')}
        noindex
      />
      <View style={styles.content}>
        <FlashList
          data={chats}
          renderItem={renderItem}
          estimatedItemSize={70}
          keyExtractor={item => item.id}
          showsVerticalScrollIndicator={false}
          ListFooterComponent={ListFooterComponent}
          contentContainerStyle={{ paddingBottom: 20 }}
        />
      </View>
    </View>
  );
});

function LibraryRoute() {
  return (
    <LibraryPage />
  );
}

export default React.memo(LibraryRoute);
